import React, {useEffect, useRef, useState} from 'react';
import classes from "./wheel.module.css";
import {texts} from "../../../../translate";
import {spinWheel} from "../../../../utils/api";
import paw from "../../../../images/paw.svg";

const CX = 200
const CY = 200
const R = 172
const WEIGHTS = [1667, 2000, 1666, 750, 1449, 1667, 750, 50, 1]
const WEIGHT_TOTAL = WEIGHTS.reduce((sum, item) => sum + item, 0)

const SECTORS = [
    {id: 'nothing', icon: 'none', fill: '#1B2A3A'},
    {id: 'refund', icon: 'refund', fill: '#2E4459'},
    {id: 'nothing2', icon: 'none', fill: '#1B2A3A'},
    {id: 'free_spin', icon: 'spin', fill: '#3E6280'},
    {id: 'paws_100', icon: 'coins', fill: '#C08A3E'},
    {id: 'nothing3', icon: 'none', fill: '#1B2A3A'},
    {id: 'free_spin2', icon: 'spin', fill: '#3E6280'},
    {id: 'paws_1000', icon: 'coins', fill: '#E0B15E'},
    {id: 'paws_10000', icon: 'coins', fill: '#F5DFA6'},
]

const sectorRange = (index) => {
    const startWeight = WEIGHTS.slice(0, index).reduce((sum, item) => sum + item, 0)
    const offset = -((WEIGHTS[0] / WEIGHT_TOTAL) * 180)
    const start = offset + (startWeight / WEIGHT_TOTAL) * 360
    const sweep = (WEIGHTS[index] / WEIGHT_TOTAL) * 360
    return {start, sweep, mid: start + sweep / 2}
}

const toRad = (deg) => ((deg - 90) * Math.PI) / 180
const pt = (deg, r) => [CX + r * Math.cos(toRad(deg)), CY + r * Math.sin(toRad(deg))]

const sectorPath = (index) => {
    const {start, sweep} = sectorRange(index)
    const end = start + sweep
    const [x1, y1] = pt(start, R)
    const [x2, y2] = pt(end, R)
    const large = sweep > 180 ? 1 : 0
    return `M${CX} ${CY} L${x1.toFixed(2)} ${y1.toFixed(2)} A${R} ${R} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)} Z`
}

const Icon = ({type}) => {
    if (type === 'none') {
        return (
            <g>
                <circle r="13" fill="none" stroke="#7fd7ff" strokeWidth="2.4"/>
                <path d="M-8 8 L8 -8" stroke="#7fd7ff" strokeWidth="2.4"/>
            </g>
        )
    }
    if (type === 'shield') {
        return (
            <g>
                <path d="M0 -14 L12 -8 V2 C12 10 6 14 0 16 C-6 14 -12 10 -12 2 V-8 Z" fill="none" stroke="#7fd7ff" strokeWidth="2"/>
                <path d="M-5 1 L5 -7" stroke="#ffd36a" strokeWidth="2"/>
            </g>
        )
    }
    if (type === 'empty') {
        return (
            <g>
                <path d="M0 -12 L12 -4 V10 L0 18 L-12 10 V-4 Z" fill="none" stroke="#7fd7ff" strokeWidth="2"/>
                <path d="M0 -12 V4 L12 10 M0 4 L-12 10" stroke="#7fd7ff" strokeWidth="1.6"/>
            </g>
        )
    }
    if (type === 'spin') {
        return (
            <g>
                <path d="M-11 -4 A12 12 0 1 1 -11 4" fill="none" stroke="#5CE0FF" strokeWidth="2.6" strokeLinecap="round"/>
                <path d="M-11 -4 L-5 -8 M-11 -4 L-14 2" fill="none" stroke="#5CE0FF" strokeWidth="2.6" strokeLinecap="round"/>
                <path d="M11 4 A12 12 0 1 1 11 -4" fill="none" stroke="#5CE0FF" strokeWidth="2.6" strokeLinecap="round"/>
                <path d="M11 4 L5 8 M11 4 L14 -2" fill="none" stroke="#5CE0FF" strokeWidth="2.6" strokeLinecap="round"/>
            </g>
        )
    }
    if (type === 'refund') {
        return (
            <g>
                <image href={paw} x="-16" y="-18" width="26" height="26"/>
                <circle cx="8" cy="8" r="8" fill="#12305c" stroke="#5CE0FF" strokeWidth="1.6"/>
                <path d="M5 8 A4 4 0 1 1 8 12" fill="none" stroke="#5CE0FF" strokeWidth="1.6" strokeLinecap="round"/>
            </g>
        )
    }
    return (
        <g>
            <image href={paw} x="-18" y="-8" width="24" height="24"/>
            <image href={paw} x="-4" y="-14" width="22" height="22"/>
        </g>
    )
}

const WheelDisc = ({lang, compact = false, uid = 'wheel'}) => {
    const labels = texts[lang].wheel.sectors
    return (
        <svg viewBox="0 0 400 400" className={classes.discSvg} aria-hidden="true">
            <defs>
                <radialGradient id={`${uid}Glow`} cx="50%" cy="45%" r="65%">
                    <stop offset="0%" stopColor="#1a4d8c"/>
                    <stop offset="55%" stopColor="#071428"/>
                    <stop offset="100%" stopColor="#030914"/>
                </radialGradient>
                <linearGradient id={`${uid}Gold`} x1="0" y1="0" x2="1" y2="1">
                    <stop offset="0%" stopColor="#FFE08A"/>
                    <stop offset="45%" stopColor="#C7922B"/>
                    <stop offset="100%" stopColor="#F6D36A"/>
                </linearGradient>
                <filter id={`${uid}Neon`} x="-20%" y="-20%" width="140%" height="140%">
                    <feGaussianBlur stdDeviation="2.2" result="blur"/>
                    <feMerge>
                        <feMergeNode in="blur"/>
                        <feMergeNode in="SourceGraphic"/>
                    </feMerge>
                </filter>
            </defs>
            <circle cx={CX} cy={CY} r="198" fill="#06101f" stroke="#2ee0ff" strokeWidth="6"/>
            <circle cx={CX} cy={CY} r="190" fill="none" stroke={`url(#${uid}Gold)`} strokeWidth="10"/>
            <circle cx={CX} cy={CY} r="182" fill={`url(#${uid}Glow)`}/>
            {SECTORS.map((sector, index) => {
                const {mid: angle, sweep} = sectorRange(index)
                if (sweep < 2) return <path key={`${sector.id}-${index}`} d={sectorPath(index)} fill={sector.fill} stroke="#4DE7FF" strokeWidth="1"/>
                const [ix, iy] = pt(angle, compact ? 96 : 86)
                const [tx, ty] = pt(angle, 134)
                const lines = labels[index].split('\n')
                return (
                    <g key={`${sector.id}-${index}`}>
                        <path d={sectorPath(index)} fill={sector.fill} stroke="#4DE7FF" strokeWidth="2.2" filter={`url(#${uid}Neon)`}/>
                        <g transform={`translate(${ix} ${iy}) rotate(${angle})`}>
                            <Icon type={sector.icon}/>
                        </g>
                        {!compact && lines.map((line, lineIndex) => (
                            <text
                                key={`${sector.id}-${line}`}
                                x={tx}
                                y={ty + (lineIndex - (lines.length - 1) / 2) * 14}
                                fill="#fff"
                                fontSize="12"
                                fontWeight="700"
                                textAnchor="middle"
                                dominantBaseline="middle"
                                transform={`rotate(${angle} ${tx} ${ty})`}
                            >
                                {line}
                            </text>
                        ))}
                    </g>
                )
            })}
            {Array.from({length: 18}).map((_, index) => {
                const angle = index * 20
                const [x, y] = pt(angle, 190)
                return <circle key={angle} cx={x} cy={y} r={index % 2 ? 3.2 : 4.2} fill="#d7b056" stroke="#7a5410" strokeWidth="0.8"/>
            })}
            <circle cx={CX} cy={CY} r="46" fill="#071428" stroke="#5CE0FF" strokeWidth="4"/>
            <circle cx={CX} cy={CY} r="36" fill="#0b1c38" stroke="#F6D36A" strokeWidth="2"/>
            <path d="M200 176 L222 216 H178 Z" fill="none" stroke="#9BE8FF" strokeWidth="4" strokeLinejoin="round"/>
            <path d="M200 184 L216 212 H184 Z" fill="#16345f"/>
        </svg>
    )
}

const CloseIcon = ({onClick}) => (
    <svg className={classes.close} onClick={onClick} xmlns="http://www.w3.org/2000/svg" width="40" height="40" viewBox="0 0 40 40" fill="none">
        <path fillRule="evenodd" clipRule="evenodd" d="M35 20C35 21.9698 34.612 23.9204 33.8582 25.7403C33.1044 27.5601 31.9995 29.2137 30.6066 30.6066C29.2137 31.9995 27.5601 33.1044 25.7403 33.8582C23.9204 34.612 21.9698 35 20 35C18.0302 35 16.0796 34.612 14.2597 33.8582C12.4399 33.1044 10.7863 31.9995 9.3934 30.6066C8.00052 29.2137 6.89563 27.5601 6.14181 25.7403C5.38799 23.9204 5 21.9698 5 20C5 16.0218 6.58035 12.2064 9.3934 9.3934C12.2064 6.58035 16.0218 5 20 5C23.9782 5 27.7936 6.58035 30.6066 9.3934C33.4196 12.2064 35 16.0218 35 20ZM12.155 27.845C11.8425 27.5325 11.667 27.1086 11.667 26.6667C11.667 26.2247 11.8425 25.8009 12.155 25.4883L17.6433 20L12.155 14.5117C11.8514 14.1973 11.6834 13.7763 11.6872 13.3393C11.691 12.9023 11.8663 12.4843 12.1753 12.1753C12.4843 11.8663 12.9023 11.691 13.3393 11.6872C13.7763 11.6834 14.1973 11.8514 14.5117 12.155L20 17.6433L25.4883 12.155C25.6421 11.9958 25.826 11.8688 26.0293 11.7815C26.2327 11.6941 26.4514 11.6482 26.6727 11.6462C26.894 11.6443 27.1134 11.6865 27.3183 11.7703C27.5231 11.8541 27.7092 11.9779 27.8657 12.1343C28.0221 12.2908 28.1459 12.4769 28.2297 12.6817C28.3135 12.8866 28.3557 13.106 28.3538 13.3273C28.3518 13.5486 28.3058 13.7673 28.2185 13.9707C28.1312 14.174 28.0042 14.3579 27.845 14.5117L22.3567 20L27.845 25.4883C28.1486 25.8027 28.3166 26.2237 28.3128 26.6607C28.309 27.0977 28.1337 27.5157 27.8247 27.8247C27.5157 28.1337 27.0977 28.309 26.6607 28.3128C26.2237 28.3166 25.8027 28.1486 25.4883 27.845L20 22.3567L14.5117 27.845C14.1991 28.1575 13.7753 28.333 13.3333 28.333C12.8914 28.333 12.4675 28.1575 12.155 27.845Z" fill="#4C4F55"/>
    </svg>
)

const Wheel = ({lang, balance, setBalance, freeSpins: freeSpinsProp = 0, onSpinResult}) => {
    const t = texts[lang].wheel
    const [open, setOpen] = useState(false)
    const [freeSpins, setFreeSpins] = useState(freeSpinsProp || 0)
    const [rotation, setRotation] = useState(0)
    const [spinning, setSpinning] = useState(false)
    const [result, setResult] = useState(null)
    const [error, setError] = useState('')
    const rotationRef = useRef(0)

    useEffect(() => {
        setFreeSpins(freeSpinsProp || 0)
    }, [freeSpinsProp])

    useEffect(() => {
        rotationRef.current = rotation
    }, [rotation])

    const canSpin = !spinning && (freeSpins > 0 || Number(balance) >= 50)

    const close = () => {
        if (spinning) return
        setOpen(false)
        setError('')
    }

    const onSpin = async () => {
        if (!canSpin) {
            setError(t.needCoins)
            return
        }
        setError('')
        setResult(null)
        setSpinning(true)
        navigator.vibrate?.(40)
        let res
        try {
            res = await spinWheel()
        } catch {
            setSpinning(false)
            setError(t.error)
            return
        }
        if (res?.error) {
            setSpinning(false)
            setError(res.detail === 'not_enough_paws' || res.detail === 'not_enough_coins' ? t.needCoins : (res.detail || t.error))
            return
        }
        setBalance(res.balance)
        setFreeSpins(res.free_spins)
        onSpinResult?.(res)
        const jitter = Math.min(8, sectorRange(res.sector_index).sweep / 4) * (Math.random() * 2 - 1)
        const target = -sectorRange(res.sector_index).mid + jitter
        const current = rotationRef.current
        const currentMod = ((current % 360) + 360) % 360
        const targetMod = ((target % 360) + 360) % 360
        let delta = targetMod - currentMod
        if (delta <= 0) delta += 360
        const next = current + 360 * 6 + delta
        setRotation(next)
        window.setTimeout(() => {
            setSpinning(false)
            setResult(res)
            navigator.vibrate?.(30)
        }, 5000)
    }

    return (
        <>
            <button type="button" data-wheel-fab className={classes.fab} onClick={() => setOpen(true)} aria-label={t.title} style={{display: open ? 'none' : 'block'}}>
                <span className={classes.fabDisc}>
                    <WheelDisc lang={lang} compact uid="fab"/>
                </span>
                {freeSpins > 0 ? <span className={classes.fabBadge}>{freeSpins}</span> : null}
            </button>
            {open && (
                <div className={classes.overlay}>
                    <div className={classes.panel}>
                        <div className={classes.top}>
                            <div className={classes.title}>{t.title}</div>
                            <span className={classes.balanceChip}>{t.balance}: {Number(balance || 0).toLocaleString('ru-RU')} {texts[lang].balance.text2}</span>
                            <CloseIcon onClick={close}/>
                        </div>
                        <div className={classes.stage}>
                            <div className={classes.pointer}/>
                            <div
                                className={`${classes.disc} ${spinning ? classes.discSpin : ''}`}
                                style={{transform: `rotate(${rotation}deg)`}}
                            >
                                <WheelDisc lang={lang} uid="main"/>
                            </div>
                        </div>
                        <div className={classes.odds}>
                            <div className={classes.oddsHead}>
                                <span>{t.odds}</span>
                                <span>{t.sectorsCount}</span>
                            </div>
                            {(texts[lang].wheel.oddsRows || [
                                [lang==='ru'?'Ничего · 3 сектора':'Nothing · 3 sectors', '50 %'],
                                [lang==='ru'?'Возврат 50 лап':'Return 50 paws', '20 %'],
                                [lang==='ru'?'Бесплатный спин · 2 сектора':'Free spin · 2 sectors', '15 %'],
                                [lang==='ru'?'100 лап':'100 paws', '14,49 %'],
                                [lang==='ru'?'1 000 лап':'1,000 paws', '0,5 %'],
                                [lang==='ru'?'10 000 лап':'10,000 paws', '0,01 %'],
                            ]).map((row) => (
                                <div className={classes.oddsRow} key={row[0]}><span>{row[0]}</span><span>{row[1]}</span></div>
                            ))}
                        </div>
                        <div className={classes.cost}>
                            {freeSpins > 0 ? `${t.spinFree} (${freeSpins})` : t.cost}
                        </div>
                        {result && !spinning && (
                            <div className={`${classes.prize} ${result.payout > 0 || result.free_spin ? classes.prizeWin : ''}`}>
                                {lang === 'ru' ? result.title : result.title_en}
                                {result.free_spin ? ` · ${t.extraSpin}` : ''}
                            </div>
                        )}
                        {error && <div className={classes.error}>{error}</div>}
                        <button
                            type="button"
                            className={`${classes.spinBtn} ${canSpin ? '' : classes.spinBtnOff}`}
                            onClick={onSpin}
                            disabled={!canSpin}
                        >
                            {spinning ? t.spinning : (freeSpins > 0 ? `${t.spinFree} (${freeSpins})` : t.spin)}
                        </button>
                        <p className={classes.legal}>{t.legal}</p>
                    </div>
                </div>
            )}
        </>
    )
}

export default Wheel
